import { Box, Icon } from '@zillow/constellation-10';

import { LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';

import { useScreenSize } from '../../../../hooks/useScreenSize';
import { getConstellationIcon } from '../../../../utils/iconUtils';

import SimpleCard from '../../../../components/SimpleCard';

import { singleTextLineStyles } from '../../../../styles/text';

import { CLICK_NEXT_STEPS_RECOMMENDATIONS_EVENT_ID } from '../../../../analytics/events/nextStepsRecommendations';

import type { CssProperties } from '@/styled-system/types';
import type { ParagraphPropsInterface, TextPropsInterface } from '@zillow/constellation-10';
import type { TileType } from '../../../../model/NextSteps';

const getIcon = (icon: string, v10Icon?: string) => {
  const iconName = v10Icon || icon;
  const IconComponent = getConstellationIcon({ iconName });

  if (!IconComponent) {
    return null;
  }

  return <Icon css={{ color: 'icon.accent.blue.hero' }} size={{ base: 'md', lg: 'lg' }} render={<IconComponent />} />;
};

type Props = {
  title?: string;
  titleFontType?: ParagraphPropsInterface['textStyle'];
  titleFontColor?: CssProperties['color'];
  titleMarginBottom?: '400' | '200';
  tileFontType?: TextPropsInterface['textStyle'];
  tileSubtitleFontType?: ParagraphPropsInterface['textStyle'];
  tileList?: Array<TileType>;
  onTileClick?: ({ sectionTitle, id, url }: { sectionTitle: string; id: string; url: string }) => void;
  isLoading?: boolean;
};

const Section = ({
  tileList = [],
  title = 'Section',
  titleFontColor = 'text.neutral',
  titleFontType = 'body',
  titleMarginBottom = '200',
  tileFontType = 'body',
  tileSubtitleFontType = 'body-xs',
  onTileClick = undefined,
  isLoading = false,
}: Props) => {
  const { isScreenDesktop } = useScreenSize();
  const withChevron = isScreenDesktop;

  const tileListLength = tileList?.length || 0;

  const handleClick = ({ id, url, sectionTitle }: { id: string; url: string; sectionTitle: string }) => {
    if (onTileClick) {
      onTileClick({ sectionTitle, id, url });
    }
  };

  const renderCard = (item: TileType, sectionTitle: string) => {
    const { id, title: itemTitle, subtitle, icon, url, v10Icon } = item;
    return (
      <SimpleCard
        withChevron={withChevron}
        href={url || undefined}
        linkLabel={itemTitle}
        onClick={() => {
          handleClick({ id, url, sectionTitle });
        }}
        isLoading={isLoading}
        data-eventid={CLICK_NEXT_STEPS_RECOMMENDATIONS_EVENT_ID}
      >
        <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', height: { base: '800', lg: '1200' } }}>
          <Box css={{ flexShrink: 0, display: 'flex' }}>
            <Box css={{ marginRight: { base: '200', lg: '600' }, display: { base: 'none', md: 'inline-flex' } }}>
              {isLoading ? (
                <Box css={{ width: 'icon.lg', height: 'icon.lg', ...shimmerStyles }} data-loading={true} />
              ) : (
                getIcon(icon, v10Icon)
              )}
            </Box>
          </Box>
          {/* oxlint-disable-next-line constellation/no-raw-spacing-values */}
          <Box css={{ minWidth: '1px', width: '100%' }}>
            <LoadingText
              textStyle={tileFontType}
              title={itemTitle}
              isLoading={isLoading}
              loadingWidth="60%"
              css={{ textStyle: { base: tileFontType, lg: 'body-sm-bold' }, ...singleTextLineStyles }}
            >
              {itemTitle}
            </LoadingText>
            {subtitle && (
              <LoadingParagraph
                textStyle={tileSubtitleFontType}
                title={subtitle}
                isLoading={isLoading}
                loadingWidth="60%"
                css={{ textStyle: { base: tileSubtitleFontType, lg: 'body-sm' }, ...singleTextLineStyles }}
              >
                {subtitle}
              </LoadingParagraph>
            )}
          </Box>
        </Box>
      </SimpleCard>
    );
  };

  const renderTile = ({ item, index }: { item: TileType; index: number }) => {
    return (
      <Box key={index} data-testid="TileItem">
        {renderCard(item, title)}
      </Box>
    );
  };

  return (
    <Box
      data-testid="Section"
      css={{
        marginBottom: { base: '400', lg: '600', xxl: '1000' },
        '&:last-child': {
          marginBottom: 0,
        },
      }}
    >
      <LoadingParagraph
        textStyle={titleFontType}
        loadingWidth="50%"
        isLoading={isLoading && !title}
        css={{ color: titleFontColor, marginBottom: titleMarginBottom }}
      >
        {title}
      </LoadingParagraph>

      <Box
        data-testid="TileListContainer"
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: tileListLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
            xxl: `repeat(1, minmax(0,1fr))`,
          },
          gap: { base: '200', lg: '400' },
        }}
      >
        {tileList && tileList.map((item, index) => renderTile({ item, index }))}
      </Box>
    </Box>
  );
};

export default Section;
